import JSCookie from 'js-cookie';
import { NextComponentType, NextPageContext } from 'next';
import { useAmp } from 'next/amp';
import { AppInitialProps, AppProps, NextWebVitalsMetric } from 'next/app';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { Store } from 'redux';
import { PAGES_WITHOUT_SUBSCRIPTION } from 'src/constants/routes';
import { getSiteSettings } from 'src/selectors/sitesettings';

import { ThemeName, ThemeNames } from '@sravni/design-system-theme';
import { IDeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import { Notifications } from '@sravni/react-design-system';
import { Footer } from '@sravni/react-footer';
import { PreFooter } from '@sravni/react-pre-footer';
import { AppAnalyticsPixel } from '@sravni/react-ugc/lib/AppAnalyticsPixel';
import { DeviceInfoProvider, ThemeProvider, useBoolean, useLocalStorage, useStyles } from '@sravni/react-utils';
import { subscribeExponeaNotification } from '@sravni/ugc/lib/helpers/exponeaNotification';
import { subscribeToNotifications } from '@sravni/ugc/lib/utils/notificationEventBus';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';
import { sentryInit } from '@sravni/ugc/lib/utils/sentry/sentryInit';
import { formatUserData } from '@sravni/utils/lib/auth';

import FingerprintJS from '@fingerprintjs/fingerprintjs';

import { CommonHeadComponent } from '../components/CommonHead';
import Header from '../components/Header';
import { IdleSubscriptionPopup } from '../components/IdleSubscriptionPopup';
import { Overlay } from '../components/Overlay';
import { SearchBlock } from '../components/SearchBlock';
import Seo from '../components/Seo';
import { TopBanner } from '../components/TopBanner';
import { REDUX_STORE } from '../constants/common';
import { sendSpaIsReadyEvent } from '../helpers/analytics';
import { errorCallback } from '../helpers/errorCallback';
import { getCacheSnapshot } from '../helpers/getCacheSnapshot';
import { IRouterQuery } from '../interfaces/news';
import ConfigProvider from '../providers/config';
import StoreProvider from '../providers/store';
import { IGlobalState } from '../reducers';
import { setCurrentLocation } from '../reducers/locations';
import { setCanLoadingMore } from '../reducers/news';
import { setSettings } from '../reducers/sitesettings';
import { getSurvey, setSurvey } from '../reducers/survey';
import { setFingerprint, setUser } from '../reducers/user';
import { getWhitelabel, setWhitelabel } from '../reducers/whitelabel';
import { getWidget, setWidget } from '../reducers/widget';
import { getMetadata } from '../selectors/metadata';
import { getFingerprint, getIsUserLogged, getUser } from '../selectors/users';
import { initializeStore } from '../store';
import styles from '../styles/page.module.scss';
import { App } from '../types';
import { onChangeActivityPageTimer } from '../utils/changeActivityPageTimer';
import { isProdBuild } from '../utils/environment';
import { isServer } from '../utils/isServer';
import queue from '../utils/queue';
import { isUseSubscription } from '../utils/subscription';
import '@sravni/design-system-theme/lib/globalStyles.css';
import '../styles/article/styles.scss';
import '../styles/global.scss';

import '@sravni/ugc/lib/theme/articleTypography.scss';
import 'intersection-observer';

export const config = { amp: 'hybrid' };

function getOrCreateStore(initialState?: IGlobalState): Store<IGlobalState> {
  if (isServer) {
    return initializeStore(initialState);
  }

  if (!window[REDUX_STORE]) {
    window[REDUX_STORE] = initializeStore(initialState);
  }

  return window[REDUX_STORE];
}

interface IProps extends AppProps {
  realPath: string;
  initialReduxState: IGlobalState;
  deviceInfo: IDeviceInfo;
  theme: ThemeNames;
}

export interface PageWithSubscriptionPopupProps {
  subscriptionPopupState: [number, (value: number) => void];
}

const isDebagMode = !isServer && localStorage.getItem('UGC') === 'debug';

const reloadWindow = () => {
  if (window.location.href.includes('?upd')) {
    const url = new URL(window.location.href);
    url.searchParams.delete('upd');
    window.location.replace(url.toString());
  } else {
    window.location.replace(`${window.location.href}?upd`);
  }
};

const MyApp: NextComponentType<App.ReduxAppContext<IRouterQuery>, AppInitialProps, IProps> = ({
  Component,
  pageProps,
  initialReduxState,
  deviceInfo,
  theme,
}) => {
  const cx = useStyles(styles);
  const { asPath } = useRouter();
  const [pathname] = asPath.split('?');
  const reduxStore = getOrCreateStore(initialReduxState);
  const { getState, dispatch } = reduxStore;
  const stateFromRedux = getState();
  const user = getUser(stateFromRedux);
  const sitesettings = getSiteSettings(stateFromRedux);
  const metadata = getMetadata(pathname)(stateFromRedux);
  const { publicRuntimeConfig } = getConfig();
  const isWl = getWhitelabel(stateFromRedux);
  const isWidget = getWidget(stateFromRedux);
  const isSurvey = getSurvey(stateFromRedux);
  const isAmp = useAmp();
  const subId = user?.sub || undefined;
  const isUserLogged = getIsUserLogged(stateFromRedux);

  const isPageWithoutSeoThings = isWidget || isSurvey;
  const pageWithSubscription = PAGES_WITHOUT_SUBSCRIPTION.every((el) => !asPath.includes(el));
  const isSearchPage = asPath.includes('/search');
  const isMainPage = /^\/mag\/?(\?.*)?$/.test(asPath);

  const isShowSnapshotButton = isDebagMode;

  const [searchActive, setSearchActive] = useBoolean(false);
  const [isScrolled, setScrolled] = useBoolean(false);

  const subscriptionPopupState = useLocalStorage('subscriptionPopupTime', 0);

  const handleScroll = () => {
    if (window.scrollY === 0) {
      setScrolled.on();
    }
  };

  useEffect(() => {
    if (!isSearchPage) {
      const remove = onChangeActivityPageTimer(reloadWindow);
      return remove;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSearchPage) {
      document.addEventListener('scroll', handleScroll);

      if (window.scrollY === 0) {
        document.removeEventListener('scroll', handleScroll);
      }

      return () => document.removeEventListener('scroll', handleScroll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setCanLoadingMore(true));
  });

  useEffect(() => {
    const fp = getFingerprint(getState());

    if (fp === '-1') {
      FingerprintJS.load()
        .then((fp) => fp.get())
        .then((result) => {
          const id = result.visitorId;

          dispatch(setFingerprint(id));
          JSCookie.set('_FP_', id);
        });
    }

    const removeNotificationsSubscription = subscribeToNotifications(errorCallback);

    if (isProdBuild) {
      queue.push(() => {
        sentryInit({
          sentryDSN: publicRuntimeConfig.sentryDSN,
          environment: publicRuntimeConfig.environment,
          release: publicRuntimeConfig.release,
          user,
        });
      });
    }

    queue.push(() => sendSpaIsReadyEvent(deviceInfo));

    return () => {
      removeNotificationsSubscription();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isProdBuild) {
      queue.push(() => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker
            .register('/service-worker.js', { updateViaCache: 'none' })
            .then(() => {
              subscribeExponeaNotification(user);
            })
            .catch((err) => {
              console.warn('Service worker registration failed', err.message);
            });
        }
      });
    }
  }, [user]);

  const handleToggleOverlay = (value: 'on' | 'off') => {
    if (value === 'on') setSearchActive.on();
    else setSearchActive.off();
  };

  const pathsWithIOSApp = new RegExp('(^/text/)|(^/mag)|(^/novost/)');
  const routeForIOSSmartBanner = !isAmp && !isWl && pathsWithIOSApp.test(asPath);

  const page = isAmp ? (
    <div className={styles['wrapper']}>
      <Component {...pageProps} subscriptionPopupState={subscriptionPopupState} />
    </div>
  ) : (
    <>
      {!isPageWithoutSeoThings && (
        <>
          <CommonHeadComponent routeForIOSSmartBanner={routeForIOSSmartBanner} subId={subId} />
          {routeForIOSSmartBanner && <AppAnalyticsPixel />}
        </>
      )}
      {!isWl && !isPageWithoutSeoThings && <Seo />}

      <div className={cx('wrapper', searchActive && 'wrapperOverlaid')}>
        {!isWl && !isPageWithoutSeoThings && (
          <div className={cx('headerWrapper', isScrolled && 'headerWrapper--scrolled')}>
            <Header />
            <TopBanner />
          </div>
        )}
        {/* для дебага кэшей */}
        {isShowSnapshotButton && (
          <button onClick={getCacheSnapshot} name={'GET CACHE SNAPSHOT'}>
            GET CACHE SNAPSHOT
          </button>
        )}
        {!isWl && isMainPage && (
          <SearchBlock toggleOverlay={handleToggleOverlay} topBlock topBlockActive={searchActive} />
        )}
        <Overlay
          overlaying={searchActive}
          toggleOverlay={handleToggleOverlay}
          children={<Component {...pageProps} subscriptionPopupState={subscriptionPopupState} />}
        />
      </div>

      {!isWl && !isPageWithoutSeoThings && !searchActive && (
        <>
          <PreFooter columns={metadata?.prefooter || []} className={styles.prefooter} />
          <Footer
            menuLinks={sitesettings.footer.menu}
            socialLinks={sitesettings.footer.socialLinks}
            support={sitesettings.footer.support}
          />
          <Notifications />
          {pageWithSubscription && isUseSubscription && !isUserLogged && (
            <IdleSubscriptionPopup subscriptionPopupState={subscriptionPopupState} />
          )}
        </>
      )}
    </>
  );

  return (
    <Interceptor componentName="news-root-component">
      <StoreProvider store={reduxStore}>
        <DeviceInfoProvider initialInfo={deviceInfo}>
          <ThemeProvider initialTheme={isWl ? ThemeName.lager : theme}>
            <ConfigProvider>{page}</ConfigProvider>
          </ThemeProvider>
        </DeviceInfoProvider>
      </StoreProvider>
    </Interceptor>
  );
};

MyApp.getInitialProps = async ({ Component, ctx }: App.ReduxAppContext<IRouterQuery>) => {
  let pageProps = {};
  let deviceInfo: IDeviceInfo = { phone: false, tablet: false, isAppleMobile: false };
  let theme: ThemeNames = ThemeName.lager;

  const reduxStore = getOrCreateStore();
  ctx.store = reduxStore;
  const { wl, widget, survey } = ctx.query;

  if (wl === 'true' || wl === 'wl') {
    reduxStore.dispatch(setWhitelabel(true));
  }

  if (widget === 'true' || widget === 'widget') {
    reduxStore.dispatch(setWidget(true));
  }

  if (survey === 'true' || survey === 'survey') {
    reduxStore.dispatch(setSurvey(true));
  }

  if (isServer) {
    const userFromCtx = ctx.req.__USER__;
    if (userFromCtx) {
      reduxStore.dispatch(
        setUser({
          ...formatUserData(userFromCtx),
          email: userFromCtx.email,
          email_verified: userFromCtx.email_verified,
        }),
      );
    }
    if (ctx.req.__SITE_SETTINGS__) {
      reduxStore.dispatch(setSettings(ctx.req.__SITE_SETTINGS__));
    }
    if (ctx.req.__SELECTED_LOCATION__) {
      reduxStore.dispatch(setCurrentLocation(ctx.req.__SELECTED_LOCATION__));
    }
    if (ctx.req.__DEVICE_INFO__) {
      deviceInfo = ctx.req.__DEVICE_INFO__;
    }
    if (ctx.req.__THEME__) {
      theme = ctx.req.__THEME__;
    }
  }

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx as NextPageContext);
  }

  return {
    pageProps,
    initialReduxState: reduxStore.getState(),
    realPath: ctx.req?.url || '',
    deviceInfo,
    theme,
  };
};

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (!isServer) {
    window.__NEXT_WEB_VITALS__ = metric;
  }
}

export default MyApp;
